import { MasonControls, MasonNamespaces } from "lib/mason";
import { ElementLayout } from "schemas/elements";

export interface FormInstance {
	instanceId: string;
	readOnly: boolean;
	formLabel: string;
	data: FormData;
	form: RootViewElementDefinition;
	"@controls": MasonControls;
	"@namespaces": MasonNamespaces;
	bindings: { [k: string]: string | null };
}

export type FormElements = ElementDefinition[];

export interface FormData {
	[key: string]: {
		id: string;
		elements: Array<{ [k: string]: FormInstanceDataItem }>;
		type: "entity" | "cursor" | "empty";
		dirty?: boolean;
	};
}

export type FormDataValue =
	| string
	| number
	| null
	| boolean
	| Array<string | number | null | boolean>;

export type FormInstanceDataItem = {
	value: FormDataValue;
	label?: string;
	feedback?: { [k: string]: string[] };
};

export type FormElementStatus = "error" | "valid";

export type ElementTypes =
	| "text"
	| "textarea"
	| "select"
	| "checkbox"
	| "radio"
	| "lookup"
	| "date"
	| "rootView"
	| "nestedView"
	| "multiReferenceView"
	| "legacyArrayView"
	| "panel"
	| "paragraph"
	| "header"
	| "reference"
	| "number";

// TODO: REMOVE WHEN LOOKUP IS UPDATED
export type ElementPayload = {
	filter: {
		AND?: PayloadArray;
		OR?: PayloadArray;
		NOT?: PayloadArray;
	};
	sort: {
		field: string;
		order: string;
	}[];
	"page-size": number;
	"request-uri": string;
	select: string[];
};

// TODO: REMOVE WHEN LOOKUP IS UPDATED
type PayloadArray = [
	{
		[key: string]: {
			contains: string;
			range: string;
			exact: string;
			exists: string;
		};
	}?
];

export enum operators {
	exact = "exact",
	contains = "contains",
	exists = "exists",
	range = "range",
}

export type SimpleBaseCondition = { value?: string | number | boolean | null };

type ExactCondition = SimpleBaseCondition;

type ContainsCondition = SimpleBaseCondition;

type ExistsCondition = SimpleBaseCondition;

export type RangeCondition = Partial<{
	gt: number | string;
	gte: number | string;
	lt: number | string;
	lte: number | string;
	format: string;
}>;

export type ConditionMap = {
	[operators.contains]: ContainsCondition;
	[operators.exact]: ExactCondition;
	[operators.exists]: ExistsCondition;
	[operators.range]: RangeCondition;
};

export type Condition<K extends operators> = {
	[key in K]: ConditionMap[K];
};

export type Statements =
	| Condition<operators.contains>
	| Condition<operators.exact>
	| Condition<operators.exists>
	| Condition<operators.range>;

export type QueryStatement = { [k: string]: Statements };

export enum Operators {
	NOT = "NOT",
	OR = "OR",
	AND = "AND",
}

export type Clause = {
	[key in Operators]?: Array<QueryStatement | Clause>;
};

export type QueryClause = Array<QueryStatement | Clause>;

export type CursorSortItem = {
	field?: string;
	order?: string;
};

export type CursorServicePayload = {
	filter: { AND: QueryClause };
	sort: CursorSortItem[];
	"page-size": number;
	"request-uri": string;
	select?: string[];
};

export type ViewCursorServicePayload = {
	filter: { AND: QueryClause };
	sort: CursorSortItem[];
	"page-size": number;
	"request-uri": string;
};

export type SearchConfig = {
	label: string;
	columns: { id: string; label?: string }[];
	view: {
		elements: QueryByFormInputTypes[];
	};
	payload: CursorServicePayload;
	overwriteDataWith?: string;
};

export type ViewSearchConfig = {
	columns: { id: string; label?: string }[];
	view: {
		elements: QueryByFormInputTypes[];
	};
	payload: CursorServicePayload;
};

export type ViewDefinition = {
	_id: string;
	category?: string;
	reference: {
		search: SearchConfig;
		viewLink?: string;
		createLink?: string;
		updateLink?: string;
	};
	format: number;
	viewLabel: string;
	permissions: {
		roles: string[];
		users: string[];
	};
};

export type ElementBase<T> = {
	elem: T;
	uuid: string;
	name: string;
	// Added these props in so that getElementId and getElementLabel selectors work?
	id?: string;
	label?: string;
	elementLayout?: ElementLayout;
};

type CreateStandardDef<T> = ElementBase<T> & {
	label: string;
	hint?: string;
	validation?: {
		minLength?: number;
		maxLength?: number;
		format?: string;
		allowNull?: boolean;
	};
	required?: boolean;
	disabled?: boolean;
	value?: string;
	id: string;
	elementLayout?: ElementLayout;
};

export type CreateViewDef<T> = ElementBase<T> & {
	id: string;
	elements: FormElements;
	formLayout: {
		fullWidth: boolean;
		maxWidth: number | null;
	};
};

export type CreateDisplayDef<T> = ElementBase<T> & {
	id?: string;
	value?: string;
};

export type SelectElementDefinition = CreateStandardDef<"select"> & {
	options: string[];
};

export type RadioElementDefinition = CreateStandardDef<"radio"> & {
	options: string[];
};

export type LookupElementDefinition = CreateStandardDef<"lookup"> & {
	limit?: number;
	isArray: boolean;
	search: SearchConfig;
	searchOperator?: "contains" | "exact";
	createLink?: string;
};

export type ReferenceElementDefinition = CreateStandardDef<"reference"> & {
	link?: string;
	limit?: number;
	isArray: boolean;
	search: SearchConfig;
	createLink?: string;
	updateLink?: string;
};

export type MediaElementDefinition = CreateStandardDef<"media">;

export type DateElementDefinition = CreateStandardDef<"date"> & {
	allowEmptyStringAsNull?: boolean;
};

export type CheckboxElementDefinition = CreateStandardDef<"checkbox">;

export type TextInputElementDefinition = CreateStandardDef<
	"text" | "number"
> & {
	isArray?: boolean;
	limit?: number;
};

export type TextAreaElementDefinition = CreateStandardDef<"textarea"> & {
	maxLength?: number;
};

export type RootViewElementDefinition = CreateViewDef<"rootView"> & {
	resource: string;
	id: string;
};

export type LegacyArrayViewElementDefinition =
	ElementBase<"legacyArrayView"> & {
		hint: string;
		required?: boolean;
		label: string;
		disabled: boolean;
		elements: FormElements;
		search?: SearchConfig;
		searchField?: string;
		limit?: number;
	};

export type MultiRefViewElementDefinition =
	CreateViewDef<"multiReferenceView"> & {
		hint?: string;
		label: string;
		id: string;
	};

export type NestedViewElementDefinition = CreateViewDef<"nestedView"> & {
	allowCreation: boolean;
	resource: string;
	id: string;
};

export type ParagraphElementDefinition = CreateDisplayDef<"paragraph">;

export type HeadingElementDefinition = CreateDisplayDef<"header">;

export type PanelElementDefinition = CreateDisplayDef<"panel"> & {
	label: string;
	loadCollapsed: boolean;
	elements: FormElements;
	elementLayout?: ElementLayout;
};

// Query by form search inputs
export type QueryByFormInputTypes =
	| TextInputElementDefinition
	| SelectElementDefinition
	| LookupElementDefinition
	| DateElementDefinition;

// Collate types
export type ElementDefinition =
	| TextInputElementDefinition
	| TextAreaElementDefinition
	| SelectElementDefinition
	| RadioElementDefinition
	| LookupElementDefinition
	| ReferenceElementDefinition
	| CheckboxElementDefinition
	| DateElementDefinition
	| MultiRefViewElementDefinition
	| RootViewElementDefinition
	| NestedViewElementDefinition
	| PanelElementDefinition
	| ParagraphElementDefinition
	| LegacyArrayViewElementDefinition
	| MediaElementDefinition
	| HeadingElementDefinition;
